import React from 'react';
import { Calendar, Tag } from 'lucide-react';

const ProblemCard = ({ problem, onClick }) => {
  const getDomainColor = (domain) => {
    const colors = {
      mechanical: 'badge-blue',
      software: 'badge-purple',
      electronics: 'badge-green',
      management: 'badge-yellow'
    };
    return colors[domain] || 'badge-blue';
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'badge-blue',
      under_discussion: 'badge-yellow',
      solution_decided: 'badge-purple',
      in_progress: 'badge-orange',
      completed: 'badge-green'
    };
    return colors[status] || 'badge-blue';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'badge-blue',
      medium: 'badge-yellow',
      high: 'badge-orange',
      critical: 'badge-red'
    };
    return colors[priority] || 'badge-blue';
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    return due < today;
  };

  // Handle both old localStorage format and new Supabase format
  const getDate = () => {
    return problem.date_reported || problem.dateReported || '';
  };

  const getDueDate = () => {
    return problem.due_date || problem.dueDate || '';
  };

  return (
    <div
      onClick={onClick}
      className="glass-card p-6 cursor-pointer"
      data-testid={`problem-card-${problem.id}`}
    >
      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className={`badge ${getDomainColor(problem.domain)}`} data-testid="problem-domain">
          {problem.domain.charAt(0).toUpperCase() + problem.domain.slice(1)}
        </span>
        <span className={`badge ${getStatusColor(problem.status)}`} data-testid="problem-status">
          {formatStatus(problem.status)}
        </span>
        <span className={`badge ${getPriorityColor(problem.priority)}`} data-testid="problem-priority">
          {problem.priority.charAt(0).toUpperCase() + problem.priority.slice(1)}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold mb-3 line-clamp-2" data-testid="problem-title">
        {problem.title}
      </h3>

      {/* Description */}
      <p className="text-gray-400 text-sm mb-4 line-clamp-3" data-testid="problem-description">
        {problem.description}
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-gray-400">
        <div className="flex items-center gap-2">
          <Calendar size={14} />
          <span data-testid="problem-date">{getDate()}</span>
        </div>
        {getDueDate() && (
          <div className={`flex items-center gap-1 ${isOverdue(getDueDate()) ? 'text-red-400' : 'text-gray-400'}`} data-testid="problem-due-date">
            📅 Due: {formatDate(getDueDate())}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProblemCard;
